import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Avatar,
  Divider,
  Paper,
  IconButton,
  colors
} from '@mui/material';
import {
  Home,
  Apartment,
  Article,
  People,
  CalendarToday,
  TrendingUp,
  MoreVert
} from '@mui/icons-material';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { useTheme } from '@mui/material/styles';
// Removed API imports since we're using static data

ChartJS.register(...registerables);

const MetricCard = ({ title, value, icon, color }: any) => {
  return (
    <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 3 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between">
          <Avatar sx={{ 
            bgcolor: `${color}.100`, 
            color: `${color}.600`,
            width: 48,
            height: 48
          }}>
            {icon}
          </Avatar>
          <IconButton size="small">
            <MoreVert />
          </IconButton>
        </Stack>
        <Typography variant="subtitle2" color="text.secondary" mt={1}>
          {title}
        </Typography>
        <Typography variant="h4" fontWeight="bold">
          {value}
        </Typography>
  
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const theme = useTheme();
  
  // Static dashboard data
  const dashboardData = {
    "totalProjects": 4,
    "totalVolunteers": 3,
    "totalBlogs": 6,
    "totalSubscribers": 1,
    "subscribersPerMonth": {
      "Jan": 0,
      "Feb": 0,
      "Mar": 0,
      "Apr": 0,
      "May": 0,
      "Jun": 0,
      "Jul": 0,
      "Aug": 0,
      "Sep": 1,
      "Oct": 0,
      "Nov": 0,
      "Dec": 0
    },
    "volunteersPerMonth": {
      "Jan": 0,
      "Feb": 0,
      "Mar": 0,
      "Apr": 0,
      "May": 0,
      "Jun": 0,
      "Jul": 0,
      "Aug": 0,
      "Sep": 2,
      "Oct": 1,
      "Nov": 0,
      "Dec": 0
    },
    "newSubscribers": 1
  };

  // Format metrics data
  const metrics = [
    { 
      title: 'Total Projects', 
      value: dashboardData.totalProjects, 
      icon: <Home fontSize="medium" />, 
      color: 'primary',
      change: 0
    },
    { 
      title: 'Total Volunteers', 
      value: dashboardData.totalVolunteers, 
      icon: <People fontSize="medium" />, 
      color: 'secondary',
      change: 0
    },
    { 
      title: 'Blog Articles', 
      value: dashboardData.totalBlogs, 
      icon: <Article fontSize="medium" />, 
      color: 'warning',
      change: 0
    },
    { 
      title: 'Subscribers', 
      value: dashboardData.totalSubscribers, 
      icon: <TrendingUp fontSize="medium" />, 
      color: 'info',
      change: 0
    }
  ];

  // Prepare chart data
  const months = Object.keys(dashboardData.subscribersPerMonth);
  const subscribersDataValues = Object.values(dashboardData.subscribersPerMonth);
  const volunteersDataValues = Object.values(dashboardData.volunteersPerMonth);

  // Subscribers data
  const subscribersData = {
    labels: months,
    datasets: [{
      label: 'New Subscribers',
      data: subscribersDataValues,
      backgroundColor: theme.palette.primary.main,
      borderColor: theme.palette.primary.dark,
      tension: 0.1,
      fill: true
    }]
  };

  // Volunteers data
  const volunteersData = {
    labels: months,
    datasets: [{
      label: 'New Volunteers',
      data: volunteersDataValues,
      backgroundColor: theme.palette.secondary.main,
      borderColor: theme.palette.secondary.dark,
      tension: 0.1,
      fill: true
    }]
  };

  // Recent activity data
  const recentActivities = [
    {
      title: `${dashboardData.newSubscribers} new ${dashboardData.newSubscribers === 1 ? 'subscriber' : 'subscribers'}`,
      icon: <People />,
      time: 'Recently'
    },
    {
      title: `${dashboardData.totalVolunteers} total volunteers registered`,
      icon: <People />,
      time: 'Recently'
    },
    {
      title: `${dashboardData.totalBlogs} blog articles published`,
      icon: <Article />,
      time: 'Recently'
    }
  ];

  return (
    <Box sx={{ p: 3, backgroundColor: 'background.default', minHeight: '100vh' }}>
      <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
      Dashboard Overview
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" mb={4}>
      Overview of your platform performance and statistics
      </Typography>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} mb={4}>
        {metrics.map((metric, index) => (
          <Grid item xs={6} sm={6} md={3} key={index}>
            <MetricCard {...metric} />
          </Grid>
        ))}
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3}>
        {/* Volunteers Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  Volunteer Growth
                </Typography>
                <IconButton size="small">
                  <MoreVert />
                </IconButton>
              </Stack>
              <Box sx={{ height: 300 }}>
                <Line 
                  data={volunteersData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: theme.palette.divider
                        }
                      },
                      x: {
                        grid: {
                          color: theme.palette.divider
                        }
                      }
                    }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Subscribers Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  Subscriber Growth
                </Typography>
                <IconButton size="small">
                  <MoreVert />
                </IconButton>
              </Stack>
              <Box sx={{ height: 300 }}>
                <Bar 
                  data={subscribersData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: theme.palette.divider
                        }
                      },
                      x: {
                        grid: {
                          color: theme.palette.divider
                        }
                      }
                    }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;


